import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, PackageCheck, Trash2 } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { API } from '../../App';

const FULFILLMENT_OPTIONS = ['pending_fulfillment', 'shipped', 'delivered', 'cancelled'];

const getAdminToken = () =>
  localStorage.getItem('adminToken') ||
  localStorage.getItem('admin_token') ||
  localStorage.getItem('token');

const toLabel = (value) =>
  String(value || '')
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const formatDate = (value) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
};

const getOrderAmount = (order) => {
  const paise = Number(order?.amount_paise);
  if (!Number.isNaN(paise) && order?.amount_paise !== null && order?.amount_paise !== undefined) {
    return paise / 100;
  }

  const fallback = Number(order?.amount);
  if (!Number.isNaN(fallback) && order?.amount !== null && order?.amount !== undefined) {
    return fallback;
  }

  return null;
};

const formatAmount = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '-';
  return `₹${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const getBuyerName = (order) => order?.delivery?.full_name || '-';

const getBuyerPhone = (order) => order?.buyer_phone || order?.delivery?.phone || '-';

const getDeliveryAddress = (order) => {
  const d = order?.delivery || {};
  const parts = [d.address_line1, d.address_line2, d.locality, d.city, d.state, d.pincode].filter(Boolean);
  return parts.length ? parts.join(', ') : '-';
};

const getTrackingDetails = (order) => {
  const number = order?.tracking_number;
  const url = order?.tracking_url;
  if (!number && !url) return '-';
  if (number && url) return `${number} • ${url}`;
  return number || url;
};

const getDisplayOrderId = (order) => order?.display_order_id || order?.order_id || '-';

const getBadgeClass = (status) => {
  switch (status) {
    case 'delivered':
      return 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30';
    case 'shipped':
      return 'bg-blue-500/20 text-blue-300 border border-blue-500/30';
    case 'cancelled':
      return 'bg-red-500/20 text-red-300 border border-red-500/30';
    case 'pending_fulfillment':
    default:
      return 'bg-amber-500/20 text-amber-300 border border-amber-500/30';
  }
};

export default function AdminQROrders() {
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fulfillmentFilter, setFulfillmentFilter] = useState('');
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [deletingOrderId, setDeletingOrderId] = useState('');

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [form, setForm] = useState({
    fulfillment_status: 'pending_fulfillment',
    tracking_number: '',
    tracking_url: '',
    notes: ''
  });

  const handleUnauthorized = useCallback(() => {
    toast.error('Admin session expired');
    navigate('/admin/login');
  }, [navigate]);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const token = getAdminToken();
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const params = new URLSearchParams();
      if (fulfillmentFilter) params.append('fulfillment_status', fulfillmentFilter);

      const url = params.toString() ? `${API}/admin/qr-orders?${params}` : `${API}/admin/qr-orders`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = response?.data;
      const nextOrders = Array.isArray(data)
        ? data
        : Array.isArray(data?.orders)
          ? data.orders
          : Array.isArray(data?.data)
            ? data.data
            : [];

      setOrders(nextOrders);
      setSelectedOrderIds((prev) => prev.filter((orderId) => nextOrders.some((order) => order.order_id === orderId)));
    } catch (error) {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        handleUnauthorized();
      } else {
        toast.error('Failed to load QR orders');
      }
    } finally {
      setLoading(false);
    }
  }, [fulfillmentFilter, navigate, handleUnauthorized]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const openFulfillmentModal = (order) => {
    const initialStatus = FULFILLMENT_OPTIONS.includes(order?.fulfillment_status)
      ? order.fulfillment_status
      : 'pending_fulfillment';

    setSelectedOrder(order);
    setForm({
      fulfillment_status: initialStatus,
      tracking_number: order?.tracking_number || '',
      tracking_url: order?.tracking_url || '',
      notes: order?.notes || ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    if (updating) return;
    setShowModal(false);
    setSelectedOrder(null);
  };

  const toggleOrderSelection = (orderId) => {
    setSelectedOrderIds((prev) =>
      prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedOrderIds.length === orders.length) {
      setSelectedOrderIds([]);
      return;
    }

    setSelectedOrderIds(orders.map((order) => order.order_id));
  };

  const handleUpdateFulfillment = async () => {
    if (!selectedOrder?.order_id) return;

    setUpdating(true);
    try {
      const token = getAdminToken();
      if (!token) {
        navigate('/admin/login');
        return;
      }

      await axios.patch(
        `${API}/admin/qr-orders/${selectedOrder.order_id}/fulfillment`,
        {
          fulfillment_status: form.fulfillment_status,
          tracking_number: form.tracking_number,
          tracking_url: form.tracking_url,
          notes: form.notes
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Fulfillment updated');
      closeModal();
      loadOrders();
    } catch (error) {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        handleUnauthorized();
      } else {
        toast.error(error?.response?.data?.message || 'Failed to update fulfillment');
      }
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!orderId) return;
    const confirmed = window.confirm('Delete this QR order? This cannot be undone.');
    if (!confirmed) return;

    setDeletingOrderId(orderId);
    try {
      const token = getAdminToken();
      if (!token) {
        navigate('/admin/login');
        return;
      }

      await axios.delete(`${API}/admin/qr-orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Order deleted');
      setSelectedOrderIds((prev) => prev.filter((id) => id !== orderId));
      loadOrders();
    } catch (error) {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        handleUnauthorized();
      } else {
        toast.error(error?.response?.data?.detail || 'Failed to delete order');
      }
    } finally {
      setDeletingOrderId('');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedOrderIds.length === 0) return;
    const confirmed = window.confirm(`Delete ${selectedOrderIds.length} selected QR orders? This cannot be undone.`);
    if (!confirmed) return;

    setBulkDeleting(true);
    try {
      const token = getAdminToken();
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const response = await axios.post(
        `${API}/admin/qr-orders/bulk-delete`,
        { order_ids: selectedOrderIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`${response.data.deleted_count || 0} order(s) deleted`);
      setSelectedOrderIds([]);
      loadOrders();
    } catch (error) {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        handleUnauthorized();
      } else {
        toast.error(error?.response?.data?.detail || 'Failed to delete selected orders');
      }
    } finally {
      setBulkDeleting(false);
    }
  };

  const totalAmount = useMemo(
    () =>
      orders.reduce((sum, order) => {
        const amount = getOrderAmount(order);
        return amount === null ? sum : sum + amount;
      }, 0),
    [orders]
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">QR Purchase Orders</h1>
            <p className="text-slate-400">Track payment, delivery progress and fulfillment updates</p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={fulfillmentFilter}
              onChange={(e) => setFulfillmentFilter(e.target.value)}
              className="bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="">All Fulfillment Status</option>
              {FULFILLMENT_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {toLabel(option)}
                </option>
              ))}
            </select>
            <button
              onClick={handleBulkDelete}
              disabled={selectedOrderIds.length === 0 || bulkDeleting}
              className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2 text-white disabled:cursor-not-allowed disabled:opacity-50 hover:bg-red-500 transition-colors"
            >
              {bulkDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Delete Selected{selectedOrderIds.length > 0 ? ` (${selectedOrderIds.length})` : ''}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
            <div className="text-sm text-slate-400">Total Orders</div>
            <div className="text-2xl font-semibold text-white">{orders.length}</div>
          </div>
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
            <div className="text-sm text-slate-400">Total Amount</div>
            <div className="text-2xl font-semibold text-white">{formatAmount(totalAmount)}</div>
          </div>
        </div>

        {loading && (
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl py-16 text-center">
            <Loader2 className="w-6 h-6 text-slate-400 animate-spin mx-auto" />
            <p className="text-slate-400 mt-3">Loading QR orders...</p>
          </div>
        )}

        {!loading && orders.length === 0 && (
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl py-16 text-center">
            <PackageCheck className="w-10 h-10 text-slate-500 mx-auto" />
            <p className="text-white mt-4 font-medium">No QR orders found</p>
            <p className="text-slate-400 text-sm mt-1">Try changing the fulfillment status filter.</p>
          </div>
        )}

        {!loading && orders.length > 0 && (
          <>
            <div className="md:hidden space-y-4">
              {orders.map((order) => (
                <div key={order.order_id} className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <label className="mb-2 inline-flex items-center gap-2 text-xs text-slate-400">
                        <input
                          type="checkbox"
                          checked={selectedOrderIds.includes(order.order_id)}
                          onChange={() => toggleOrderSelection(order.order_id)}
                          className="h-4 w-4 rounded border-slate-600 bg-slate-900 text-blue-600 focus:ring-blue-500"
                        />
                        Select order
                      </label>
                      <div className="text-white font-semibold">Order #{getDisplayOrderId(order)}</div>
                      {order?.display_order_id && (
                        <div className="text-[11px] text-slate-500 font-mono">Ref: {order.order_id || '-'}</div>
                      )}
                      <div className="text-xs text-slate-400">{formatDate(order.created_at || order.date)}</div>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getBadgeClass(order.fulfillment_status)}`}>
                      {toLabel(order.fulfillment_status || 'pending_fulfillment')}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-slate-400">Quantity</p>
                      <p className="text-white">{order.quantity ?? '-'}</p>
                    </div>
                    <div>
                      <p className="text-slate-400">Amount</p>
                      <p className="text-white">{formatAmount(getOrderAmount(order))}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-slate-400">Payment ID</p>
                      <p className="text-white break-all">{order.payment_id || '-'}</p>
                    </div>
                  </div>

                  <div className="text-sm space-y-1">
                    <p className="text-slate-400">Buyer</p>
                    <p className="text-white">{getBuyerName(order)}</p>
                    <p className="text-slate-300">{getBuyerPhone(order)}</p>
                  </div>

                  <div className="text-sm">
                    <p className="text-slate-400">Delivery Address</p>
                    <p className="text-white">{getDeliveryAddress(order)}</p>
                  </div>

                  <div className="text-sm">
                    <p className="text-slate-400">Tracking Details</p>
                    <p className="text-white break-all">{getTrackingDetails(order)}</p>
                  </div>

                  <div className="flex gap-2">
                    {order?.status === 'paid' ? (
                      <>
                        <button
                          onClick={() => openFulfillmentModal(order)}
                          className="flex-1 bg-blue-600 hover:bg-blue-500 text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors"
                        >
                          Update Fulfillment
                        </button>
                        <button
                          onClick={() => handleDeleteOrder(order.order_id)}
                          disabled={deletingOrderId === order.order_id}
                          className="inline-flex items-center justify-center rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {deletingOrderId === order.order_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        </button>
                      </>
                    ) : (
                      <>
                        <p className="flex-1 text-xs text-slate-500">Fulfillment updates are available after payment is marked paid.</p>
                        <button
                          onClick={() => handleDeleteOrder(order.order_id)}
                          disabled={deletingOrderId === order.order_id}
                          className="inline-flex items-center justify-center rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {deletingOrderId === order.order_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="hidden md:block bg-slate-800/50 border border-slate-700 rounded-xl overflow-x-auto">
              <table className="w-full min-w-[1280px]">
                <thead className="bg-slate-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">
                      <input
                        type="checkbox"
                        checked={orders.length > 0 && selectedOrderIds.length === orders.length}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 rounded border-slate-600 bg-slate-900 text-blue-600 focus:ring-blue-500"
                        aria-label="Select all orders"
                      />
                    </th>
                    <th className="text-left text-slate-400 px-4 py-3 text-sm font-medium">Order ID</th>
                    <th className="text-left text-slate-400 px-4 py-3 text-sm font-medium">Date</th>
                    <th className="text-left text-slate-400 px-4 py-3 text-sm font-medium">Fulfillment</th>
                    <th className="text-left text-slate-400 px-4 py-3 text-sm font-medium">Buyer</th>
                    <th className="text-right text-slate-400 px-4 py-3 text-sm font-medium">Qty</th>
                    <th className="text-right text-slate-400 px-4 py-3 text-sm font-medium">Amount</th>
                    <th className="text-left text-slate-400 px-4 py-3 text-sm font-medium">Payment ID</th>
                    <th className="text-left text-slate-400 px-4 py-3 text-sm font-medium">Delivery Address</th>
                    <th className="text-left text-slate-400 px-4 py-3 text-sm font-medium">Tracking Details</th>
                    <th className="text-right text-slate-400 px-4 py-3 text-sm font-medium">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {orders.map((order) => (
                    <tr key={order.order_id} className="hover:bg-slate-800/40">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedOrderIds.includes(order.order_id)}
                          onChange={() => toggleOrderSelection(order.order_id)}
                          className="h-4 w-4 rounded border-slate-600 bg-slate-900 text-blue-600 focus:ring-blue-500"
                          aria-label={`Select order ${getDisplayOrderId(order)}`}
                        />
                      </td>
                      <td className="px-4 py-3 text-white font-medium">
                        <p>{getDisplayOrderId(order)}</p>
                        {order?.display_order_id && (
                          <p className="text-xs text-slate-500 font-mono mt-0.5">{order.order_id || '-'}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{formatDate(order.created_at || order.date)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getBadgeClass(order.fulfillment_status)}`}>
                          {toLabel(order.fulfillment_status || 'pending_fulfillment')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-white">{getBuyerName(order)}</p>
                        <p className="text-slate-400 text-sm">{getBuyerPhone(order)}</p>
                      </td>
                      <td className="px-4 py-3 text-slate-200 text-right">{order.quantity ?? '-'}</td>
                      <td className="px-4 py-3 text-slate-200 text-right">{formatAmount(getOrderAmount(order))}</td>
                      <td className="px-4 py-3 text-slate-300 break-all">{order.payment_id || '-'}</td>
                      <td className="px-4 py-3 text-slate-300 max-w-xs break-words">{getDeliveryAddress(order)}</td>
                      <td className="px-4 py-3 text-slate-300 max-w-xs break-all">{getTrackingDetails(order)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2 items-center">
                          {order?.status === 'paid' ? (
                            <>
                              <button
                                onClick={() => openFulfillmentModal(order)}
                                className="bg-blue-600 hover:bg-blue-500 text-white rounded-lg px-3 py-1.5 text-sm font-medium transition-colors"
                              >
                                Update Fulfillment
                              </button>
                              <button
                                onClick={() => handleDeleteOrder(order.order_id)}
                                disabled={deletingOrderId === order.order_id}
                                className="inline-flex items-center justify-center rounded-lg bg-red-600 px-3 py-1.5 text-white transition-colors hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-60"
                                title="Delete order"
                              >
                                {deletingOrderId === order.order_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                              </button>
                            </>
                          ) : (
                            <>
                              <span className="text-xs text-slate-500">Awaiting payment</span>
                              <button
                                onClick={() => handleDeleteOrder(order.order_id)}
                                disabled={deletingOrderId === order.order_id}
                                className="inline-flex items-center justify-center rounded-lg bg-red-600 px-3 py-1.5 text-white transition-colors hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-60"
                                title="Delete order"
                              >
                                {deletingOrderId === order.order_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {showModal && selectedOrder && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm p-4 flex items-center justify-center">
          <div className="w-full max-w-xl bg-slate-900 border border-slate-700 rounded-2xl p-6 space-y-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-white">Update Fulfillment</h2>
                <p className="text-sm text-slate-400 mt-1">Order #{getDisplayOrderId(selectedOrder)}</p>
                {selectedOrder?.display_order_id && (
                  <p className="text-[11px] text-slate-500 font-mono mt-1">Ref: {selectedOrder.order_id || '-'}</p>
                )}
              </div>
              <button
                onClick={closeModal}
                disabled={updating}
                className="text-slate-400 hover:text-white text-sm"
              >
                Close
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-slate-300 mb-2">Fulfillment Status</label>
                <select
                  value={form.fulfillment_status}
                  onChange={(e) => setForm((prev) => ({ ...prev, fulfillment_status: e.target.value }))}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  {FULFILLMENT_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {toLabel(option)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-slate-300 mb-2">Tracking Number</label>
                <input
                  type="text"
                  value={form.tracking_number}
                  onChange={(e) => setForm((prev) => ({ ...prev, tracking_number: e.target.value }))}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="Enter tracking number"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-2">Tracking URL</label>
              <input
                type="url"
                value={form.tracking_url}
                onChange={(e) => setForm((prev) => ({ ...prev, tracking_url: e.target.value }))}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500"
                placeholder="https://..."
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-2">Notes</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                rows={4}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500"
                placeholder="Add delivery or fulfillment notes"
              />
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={closeModal}
                disabled={updating}
                className="px-4 py-2 rounded-lg border border-slate-600 text-slate-200 hover:bg-slate-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateFulfillment}
                disabled={updating}
                className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {updating && <Loader2 className="w-4 h-4 animate-spin" />}
                Save Update
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}